/**
 * @fileoverview ElkLayout布局插件 - 提供自动化图形布局功能
 *
 * 本插件基于elkjs实现LogicFlow的自动化布局功能，支持多种布局方向
 * 可自动计算节点位置和连线路径，实现整洁的图形展示
 */
import LogicFlow, { BaseNodeModel, BaseEdgeModel } from '@logicflow/core'
import elkConstructor from 'elkjs/lib/elk.bundled'
import { LayoutOptions, ElkNode } from 'elkjs/lib/elk-api'
import { processEdges } from '../utils/processEdge'
import { elkOptionMap } from './config'
import {
  alignScopedLayoutToGroup,
  applyGroupResizeAndWarnings,
  createGroupLayoutWarningState,
  getNodeSize,
  GroupLayoutOption,
  isGroupModel,
  LayoutScope,
  moveGroupDescendantsBy,
  resolveLayoutScopes,
} from '../utils/groupLayout'

import NodeConfig = LogicFlow.NodeConfig
import EdgeConfig = LogicFlow.EdgeConfig

/**
 * ElkLayout布局配置选项接口
 * @interface ElkLayoutOption
 */
export interface ElkLayoutOption extends GroupLayoutOption {
  /**
   * 布局方向
   * 'LR' | 'TB' | 'BT' | 'RL'
   */
  rankdir?: 'LR' | 'TB' | 'BT' | 'RL'
  /**
   * 对齐方式
   * 'UL' | 'UR' | 'DL' | 'DR'
   */
  align?: 'UL' | 'UR' | 'DL' | 'DR'
  /**
   * 同层节点间距
   */
  nodesep?: number
  /**
   * 层级间距
   */
  ranksep?: number
  /**
   * 图的水平边距
   */
  marginx?: number
  /**
   * 图的垂直边距
   */
  marginy?: number
  /**
   * 排版算法
   * 'network-simplex' | 'tight-tree' | 'longest-path'
   */
  ranker?: 'network-simplex' | 'tight-tree' | 'longest-path'
  /**
   * 边间距
   */
  edgesep?: number
  /**
   * 有向无环图处理算法
   * 'greedy'
   */
  acyclicer?: 'greedy'
  /**
   * 是否是默认锚点
   * true: 会根据布局方向自动计算边的路径点
   */
  isDefaultAnchor?: boolean
  /**
   * ELK 原生布局属性，用于覆盖默认配置
   */
  elkOption?: LayoutOptions
}

/**
 * ElkLayout插件接口定义
 */
export interface ElkLayoutPlugin {
  /**
   * 执行布局计算
   * @param option - 布局配置选项
   */
  layout(option: ElkLayoutOption): void
}

/**
 * ElkLayout布局类 - LogicFlow自动布局插件
 * 基于elkjs提供图的自动布局能力
 */
export class ElkLayout {
  /** 插件名称，用于在LogicFlow中注册 */
  static pluginName = 'elkLayout'

  /** LogicFlow实例引用 */
  lf: LogicFlow

  /** 当前布局配置 */
  option: ElkLayoutOption

  /**
   * 插件初始化方法，由LogicFlow自动调用
   * @param lf - LogicFlow实例
   */
  render(lf: LogicFlow) {
    this.lf = lf
  }

  /**
   * 执行布局算法，重新排列图中的节点和边
   * @param option - 布局配置选项
   */
  layout(option: ElkLayoutOption = {}) {
    const { nodes: allNodes, edges: allEdges, gridSize } = this.lf.graphModel

    // 根据网格大小调整节点间距
    let nodesep = 100
    let ranksep = 150
    if (gridSize > 20) {
      nodesep = gridSize * 2
      ranksep = gridSize * 2
    }

    // 合并默认配置和用户配置
    this.option = {
      // 默认从左到右布局
      rankdir: 'LR',
      // 默认右下角对齐
      align: 'UL',
      // 紧凑树形排名算法
      ranker: 'tight-tree',
      // 层级间距
      ranksep,
      // 同层节点间距
      nodesep,
      // 图的水平边距
      marginx: 120,
      // 图的垂直边距
      marginy: 120,
      // 用户自定义选项覆盖默认值
      ...option,
    }

    const scopes = resolveLayoutScopes(allNodes, allEdges, this.option.groupId)
    if (scopes.length === 0) return
    this.applyElkLayout(allNodes, allEdges, scopes)
  }

  /**
   * 使用 ELK 布局
   * @param nodes - 节点数据
   * @param edges - 边数据
   * @param elkOption - ELK 配置选项
   */
  async applyElkLayout(
    allNodes: BaseNodeModel[],
    allEdges: BaseEdgeModel[],
    scopes: LayoutScope[],
  ) {
    // 创建elk实例
    const elk = new elkConstructor()
    // elk布局配置
    const layoutOptions = this.convertOptionsToElk()
    // 开始elk布局
    try {
      const newGraphData = await this.convertLayoutDataToLf(
        allNodes,
        allEdges,
        scopes,
        async (scope, nodeMap) => {
          // 构造elk布局数据
          const elkGraph = {
            id: scope.groupId ?? 'root',
            children: scope.nodes.map((node) => {
              const nodeData = nodeMap.get(node.id)
              const size = nodeData
                ? getNodeSize(nodeData, new Map([[node.id, node]]))
                : node
              return {
                id: node.id,
                width: size.width || 150,
                height: size.height || 50,
              }
            }),
            edges: scope.edges.map((edge) => ({
              id: edge.id,
              sources: [edge.sourceNodeId],
              targets: [edge.targetNodeId],
            })),
          }
          return elk.layout(elkGraph, { layoutOptions })
        },
      )
      this.lf.renderRawData(newGraphData)
    } catch (error) {
      console.error('ELK layout error:', error)
    }
  }
  convertOptionsToElk(): LayoutOptions {
    // elk布局配置
    const rankdir = (this.option.rankdir ||
      'default') as keyof typeof elkOptionMap.rankdir
    const align = (this.option.align ||
      'default') as keyof typeof elkOptionMap.align
    const ranker = (this.option.ranker ||
      'default') as keyof typeof elkOptionMap.ranker
    const acyclicer = (this.option.acyclicer ||
      'default') as keyof typeof elkOptionMap.acyclicer

    const layoutOptions = {
      'elk.algorithm': 'layered',
      'elk.direction':
        elkOptionMap.rankdir[rankdir] || elkOptionMap.rankdir.default,
      'elk.layered.nodePlacement.bk.fixedAlignment':
        elkOptionMap.align[align] || elkOptionMap.align.default,
      'elk.layered.layering.strategy':
        elkOptionMap.ranker[ranker] || elkOptionMap.ranker.default,
      'elk.layered.cycleBreaking.strategy':
        elkOptionMap.acyclicer[acyclicer] || elkOptionMap.acyclicer.default,
      'elk.padding': `[top=${this.option.marginx || 20}, left=${this.option.marginy || 20}, bottom=${this.option.marginx || 20}, right=${this.option.marginy || 20}]`,
      'elk.spacing.nodeNode': `${this.option.nodesep || 50}`,
      'elk.spacing.edgeEdge': `${this.option.edgesep || 10}`,
      'elk.layered.spacing.nodeNodeBetweenLayers': `${this.option.ranksep || 100}`,
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
      ...this.option.elkOption,
    }
    return layoutOptions
  }

  async convertLayoutDataToLf(
    allNodes: BaseNodeModel[],
    allEdges: BaseEdgeModel[],
    scopes: LayoutScope[],
    runLayout: (
      scope: LayoutScope,
      nodeMap: Map<string, NodeConfig>,
    ) => Promise<ElkNode>,
  ) {
    // 存储新的节点和边数据
    const newNodes: NodeConfig[] = allNodes.map((nodeModel) => {
      const lfNode = nodeModel.getData()
      if (!lfNode) {
        throw new Error(`布局错误：找不到ID为 ${nodeModel.id} 的节点`)
      }
      return lfNode
    })
    const nodeMap = new Map(
      newNodes
        .filter((node): node is NodeConfig & { id: string } => !!node.id)
        .map((node) => [node.id, node]),
    )
    const warningState = createGroupLayoutWarningState()

    for (const scope of scopes) {
      const layoutData = await runLayout(scope, nodeMap)
      this.applyScopeLayout(allNodes, newNodes, nodeMap, scope, layoutData)
      alignScopedLayoutToGroup(allNodes, newNodes, scope.nodes, scope.groupId)
      applyGroupResizeAndWarnings(allNodes, newNodes, this.option, warningState)
    }

    const newEdges: EdgeConfig[] = processEdges(
      this.lf,
      this.option.rankdir,
      this.option.isDefaultAnchor,
      allEdges,
      newNodes,
    )

    return {
      nodes: newNodes,
      edges: newEdges,
    }
  }

  applyScopeLayout(
    allNodes: BaseNodeModel[],
    newNodes: NodeConfig[],
    nodeMap: Map<string, NodeConfig>,
    scope: LayoutScope,
    layoutData: ElkNode,
  ) {
    const layoutChildren = layoutData?.children || []

    scope.nodes.forEach((nodeModel) => {
      const lfNode = nodeMap.get(nodeModel.id)
      const newNode = layoutChildren.find((n) => n.id === nodeModel.id)
      if (
        !lfNode ||
        !newNode ||
        newNode.x === undefined ||
        newNode.y === undefined
      ) {
        throw new Error(`布局错误：找不到ID为 ${nodeModel.id} 的节点`)
      }
      // 更新节点坐标
      const size = getNodeSize(lfNode, new Map([[nodeModel.id, nodeModel]]))
      const nextX = newNode.x + size.width / 2
      const nextY = newNode.y + size.height / 2
      const dx = nextX - lfNode.x
      const dy = nextY - lfNode.y

      lfNode.x = nextX
      lfNode.y = nextY

      // 更新节点文本位置
      if (
        typeof lfNode.text === 'object' &&
        lfNode.text &&
        typeof lfNode.text.x === 'number'
      ) {
        lfNode.text.x = nextX
        lfNode.text.y = nextY
      }

      if (isGroupModel(nodeModel)) {
        moveGroupDescendantsBy(allNodes, newNodes, nodeModel.id, dx, dy)
      }
    })
  }
}
